<script lang="ts">
  import { mdiSendCheckOutline } from "@mdi/js"
  import { onDestroy, onMount, type Snippet } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import FormWrapper, { type FormContext } from "@kontextwerk/web-sdk/ui/layout/FormWrapper.svelte"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type WrongRecipientFormModel = {
    suggestedContactName: string
    suggestedContactValue: string
    message: string
  }

  let {
    onsubmit,
    onremoveOverlay = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: {
    onsubmit: (payload: {
      message?: string | null
      suggested_contact_name?: string | null
      suggested_contact_value?: string | null
    }) => Promise<void> | void
  } & OverlayHostProps = $props()

  let submitting = $state(false)
  let submitForm = $state<(() => Promise<void>) | null>(null)
  let validationError = $state<string | null>(null)

  const initialForm = (): WrongRecipientFormModel => ({
    suggestedContactName: "",
    suggestedContactValue: "",
    message: "",
  })

  const validateReport = ({ data }: { data: WrongRecipientFormModel }) => {
    if (
      !data.message.trim() &&
      !data.suggestedContactName.trim() &&
      !data.suggestedContactValue.trim()
    ) {
      validationError =
        "Bitte nennen Sie mindestens einen Hinweis oder Kontakt zum richtigen Ansprechpartner."

      return false
    }

    validationError = null

    return true
  }

  const submitReport = async (data: WrongRecipientFormModel) => {
    await onsubmit({
      message: data.message.trim() || null,
      suggested_contact_name: data.suggestedContactName.trim() || null,
      suggested_contact_value: data.suggestedContactValue.trim() || null,
    })
    onremoveOverlay?.()
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(actionButtons)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })
</script>

<div class="wrong-recipient-report overlay-form">
  <p class="intro">
    Sie haben diese Terminmitteilung erhalten, sind aber nicht die richtige Person für die
    Terminvereinbarung. Senden Sie uns bitte Hinweise zum richtigen Ansprechpartner, damit der
    Betrieb die Zuordnung prüfen kann.
  </p>
  {#if validationError}
    <p class="field-error">{validationError}</p>
  {/if}
  <FormWrapper
    theme="light"
    mode="create"
    getInitObject={initialForm}
    validateData={validateReport}
    onSubmit={submitReport}
    showReset={false}
    formSnippet={formContent}
    bind:submitting
    setSubmitFn={(fn) => (submitForm = fn)}
  />
</div>

{#snippet formContent(ctx: FormContext)}
  {@const { data, submitting } = ctx}
  <Input
    id="appointment-portal-wrong-recipient-name"
    type="text"
    placeholder="Neuer Ansprechpartner (optional)"
    bind:value={data.suggestedContactName}
    disabled={submitting}
    brightBackground={true}
  />
  <Input
    id="appointment-portal-wrong-recipient-contact"
    type="text"
    placeholder="Neuer Ansprechpartner E-Mail / Tel (optional)"
    bind:value={data.suggestedContactValue}
    disabled={submitting}
    brightBackground={true}
  />
  <Input
    id="appointment-portal-wrong-recipient-message"
    type="textarea"
    placeholder="sonstige Hinweise (optional)"
    bind:value={data.message}
    disabled={submitting}
    brightBackground={true}
  />
{/snippet}

{#snippet actionButtons()}
  <CTA
    type="primaryButton"
    icon={mdiSendCheckOutline}
    content={submitting ? "Wird gesendet" : "Hinweis senden"}
    onClick={() => submitForm?.()}
    disabled={submitting}
    fullWidth={true}
  />
{/snippet}

<style>
  .wrong-recipient-report {
    display: grid;
    gap: 1rem;
    padding-bottom: 1rem;
  }

  .intro,
  .field-error {
    margin: 0;
  }

  .intro {
    color: var(--bg-200);
    line-height: 1.45;
  }

  .field-error {
    color: #b42318;
    font-weight: 800;
  }

  :global(.wrong-recipient-report .formActions) {
    display: none;
  }
</style>
